'use client';

import { formatText, TextRole } from '@pickleballinc/lib/format-text';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { bragAchievement } from '@/utils/actions/players/edit/achievements/bragAchievement';

const BRAG_SUCCESS = formatText({
  text: 'Brag sent to your friends!',
  role: TextRole.AlertBody,
});
const BRAG_ALREADY_SENT = formatText({
  text: 'You already bragged about this achievement this week.',
  role: TextRole.ErrorMessage,
});
const BRAG_NOT_EARNED = formatText({
  text: "You can only brag about achievements you've earned.",
  role: TextRole.ErrorMessage,
});
const BRAG_GENERIC_ERROR = formatText({
  text: 'Something went wrong. Try again.',
  role: TextRole.ErrorMessage,
});

const STATUS_ALREADY_BRAGGED = 429;
const STATUS_NOT_EARNED = 404;

class BragError extends Error {
  constructor(readonly statusCode: number) {
    super(`Brag failed with status ${statusCode}`);
  }
}

const getErrorToast = (error: unknown) => {
  if (!(error instanceof BragError)) return BRAG_GENERIC_ERROR;
  if (error.statusCode === STATUS_ALREADY_BRAGGED) return BRAG_ALREADY_SENT;
  if (error.statusCode === STATUS_NOT_EARNED) return BRAG_NOT_EARNED;
  return BRAG_GENERIC_ERROR;
};

export const useBragAchievement = () => {
  const { mutate, isPending } = useMutation({
    mutationFn: async (achievementId: number) => {
      const res = await bragAchievement({ achievementId });
      if (res.error) {
        throw new BragError(res.statusCode);
      }
    },
    onSuccess: () => toast.success(BRAG_SUCCESS),
    onError: (error) => toast.error(getErrorToast(error)),
  });

  return {
    brag: (achievementId: number, onSuccess?: () => void) => mutate(achievementId, { onSuccess }),
    isPending,
  };
};
